import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { SidebarProvider } from "units/dashboard/context/SidebarProvider";
import UserSidebar from "./components/UserSidebar";
import Header from "units/dashboard/components/Header";
import Footer from "units/dashboard/components/Footer";
import { useTranslation } from "react-i18next";

import {
    requestChangePhoneOtp,
    verifyChangePhone,
} from "./services/editNumber";

// ========================================
// StarField
// ========================================
const StarField = () => {
    const [stars, setStars] = useState([]);

    useEffect(() => {
        const generatedStars = Array.from({ length: 60 }, (_, i) => ({
            id: i,
            top: Math.random() * 100,
            left: Math.random() * 100,
            size: Math.random() * 3 + 1,
            delay: Math.random() * 5,
            duration: Math.random() * 3 + 2,
            opacity: Math.random() * 0.7 + 0.3,
        }));
        setStars(generatedStars);
    }, []);

    return (
        <div className="fixed inset-0 -z-10 overflow-hidden pointer-events-none">
            <div
                className="absolute inset-0 
                           bg-gradient-to-br 
                           from-slate-50 
                           via-white 
                           to-teal-50/60
                           dark:!from-teal-950 
                           dark:!via-gray-950 
                           dark:!to-black"
            />

            <div
                className="absolute inset-0 opacity-40
                           bg-gradient-to-tr 
                           from-transparent 
                           via-amber-50/40 
                           to-transparent
                           dark:!from-transparent 
                           dark:!via-teal-900/20 
                           dark:!to-black/40"
            />

            <div className="absolute -top-32 -right-32 w-96 h-96 
                            bg-gradient-to-br from-teal-300/40 to-teal-500/20 
                            dark:!from-teal-600/30 dark:!to-teal-800/20
                            rounded-full blur-3xl animate-float" />

            <div
                className="absolute -bottom-32 -left-32 w-96 h-96 
                           bg-gradient-to-br from-amber-300/40 to-amber-500/20 
                           dark:!from-amber-600/20 dark:!to-amber-800/10
                           rounded-full blur-3xl animate-float"
                style={{ animationDelay: "500ms" }}
            />

            <div
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 
                           w-[600px] h-[600px] 
                           bg-gradient-to-br from-teal-200/30 via-amber-100/30 to-teal-200/30 
                           dark:!from-teal-800/20 dark:!via-teal-900/15 dark:!to-teal-950/20
                           rounded-full blur-3xl animate-float"
                style={{ animationDelay: "300ms" }}
            />

            {stars.map((star) => (
                <div
                    key={star.id}
                    className="absolute rounded-full 
                               bg-gradient-to-br from-amber-400 to-teal-400 
                               dark:!from-amber-300 dark:!to-teal-300"
                    style={{
                        top: `${star.top}%`,
                        left: `${star.left}%`,
                        width: `${star.size}px`,
                        height: `${star.size}px`,
                        opacity: star.opacity,
                        animation: `twinkle ${star.duration}s ease-in-out ${star.delay}s infinite`,
                        boxShadow: `0 0 ${star.size * 3}px rgba(212, 175, 55, 0.6)`,
                    }}
                />
            ))}

            <style jsx>{`
                @keyframes float {
                    0%, 100% { transform: translateY(0); }
                    50% { transform: translateY(-10px); }
                }
                @keyframes twinkle {
                    0%, 100% { opacity: 0.3; transform: scale(1); }
                    50% { opacity: 1; transform: scale(1.3); }
                }
                .animate-float {
                    animation: float 3s ease-in-out infinite;
                }
            `}</style>
        </div>
    );
};

const ChangeUserNumber = () => {
    const { t, i18n } = useTranslation(["user"]);
    const isEnglish = i18n.language === "en";

    const accessToken = useSelector(
        (state) => state.auth?.accessToken
    );

    const [oldPhoneNumber, setOldPhoneNumber] = useState("");
    const [newPhoneNumber, setNewPhoneNumber] = useState("");
    const [verificationCode, setVerificationCode] = useState("");
    const [step, setStep] = useState(1);
    const [countdown, setCountdown] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [successData, setSuccessData] = useState(null);
    const [testOtp, setTestOtp] = useState(null);

    const isDevelopment =
        window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1";

    useEffect(() => {
        if (countdown <= 0) {
            return;
        }

        const timer = setInterval(() => {
            setCountdown((prev) => {
                if (prev <= 1) {
                    clearInterval(timer);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [countdown]);

    const normalizePhone = (phone) => {
        return phone.trim().replace(/^0/, "");
    };

    const formatCountdown = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;

        return `${String(minutes).padStart(
            2,
            "0"
        )}:${String(remainingSeconds).padStart(
            2,
            "0"
        )}`;
    };

    const validatePhoneNumber = (phone) => {
        return /^09[0-9]{9}$/.test(phone);
    };

    const getBackendErrorCode = (error) => {
        return (
            error?.response?.data?.code ||
            error?.response?.data?.errorCode ||
            error?.response?.data?.error?.code ||
            error?.response?.data?.detail?.code ||
            error?.code ||
            error?.errorCode ||
            error?.error?.code ||
            error?.detail?.code ||
            null
        );
    };

    const getBackendMessage = (error) => {
        if (error?.response?.data) {
            const data = error.response.data;

            if (typeof data === "string") return data;
            if (data?.message) return data.message;
            if (data?.detail?.message) return data.detail.message;
            if (typeof data?.detail === "string") return data.detail;
            if (data?.error?.message) return data.error.message;
            if (typeof data?.error === "string") return data.error;
        }

        if (typeof error === "string") return error;
        if (error?.message) return error.message;
        if (error?.detail?.message) return error.detail.message;
        if (typeof error?.detail === "string") return error.detail;
        if (error?.error?.message) return error.error.message;
        if (typeof error?.error === "string") return error.error;

        return t("changeUserNumber.errors.generic");
    };

    const getRemainingSeconds = (error) => {
        const value =
            error?.response?.data?.remainingSeconds ??
            error?.response?.data?.detail?.remainingSeconds ??
            error?.response?.data?.data?.remainingSeconds ??
            error?.remainingSeconds ??
            error?.data?.remainingSeconds;

        const number = Number(value);

        return Number.isFinite(number) && number > 0 ? number : 0;
    };

    const handleOldPhoneChange = (e) => {
        const value = e.target.value.replace(/\D/g, "");
        if (value.length <= 11) {
            setOldPhoneNumber(value);
            setErrors((prev) => ({ ...prev, oldPhoneNumber: "", general: "" }));
        }
    };

    const handleNewPhoneChange = (e) => {
        const value = e.target.value.replace(/\D/g, "");
        if (value.length <= 11) {
            setNewPhoneNumber(value);
            setErrors((prev) => ({ ...prev, newPhoneNumber: "", general: "" }));
        }
    };

    const handleOtpChange = (e) => {
        const value = e.target.value.replace(/\D/g, "");
        if (value.length <= 6) {
            setVerificationCode(value);
            setErrors((prev) => ({ ...prev, verificationCode: "", general: "" }));
        }
    };

    const validateStepOne = () => {
        const newErrors = {};

        if (!oldPhoneNumber) {
            newErrors.oldPhoneNumber = t("changeUserNumber.errors.oldPhoneRequired");
        } else if (!validatePhoneNumber(oldPhoneNumber)) {
            newErrors.oldPhoneNumber = t("changeUserNumber.errors.oldPhoneInvalid");
        }

        if (!newPhoneNumber) {
            newErrors.newPhoneNumber = t("changeUserNumber.errors.newPhoneRequired");
        } else if (!validatePhoneNumber(newPhoneNumber)) {
            newErrors.newPhoneNumber = t("changeUserNumber.errors.newPhoneInvalid");
        } else if (oldPhoneNumber === newPhoneNumber) {
            newErrors.newPhoneNumber = t("changeUserNumber.errors.samePhone");
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSendCode = async () => {
        if (countdown > 0) return;
        if (!validateStepOne()) return;

        setIsLoading(true);
        setErrors({});
        setTestOtp(null);

        const payload = {
            currentCountryCode: "+98",
            currentPhone: normalizePhone(oldPhoneNumber),
            newCountryCode: "+98",
            newPhone: normalizePhone(newPhoneNumber),
        };

        try {
            const response = await requestChangePhoneOtp(payload, accessToken);

            if (isDevelopment && response?.data !== undefined && response?.data !== null) {
                setTestOtp(String(response.data));
            }

            const remainingSeconds = Number(response?.remainingSeconds) || 120;
            setCountdown(remainingSeconds);
            setVerificationCode("");
            setStep(2);
            setErrors({});
        } catch (error) {
            const remainingSeconds = getRemainingSeconds(error);
            if (remainingSeconds > 0) setCountdown(remainingSeconds);

            setErrors({
                general: getBackendMessage(error) || t("changeUserNumber.errors.sendCodeFailed"),
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubmit = async () => {
        if (!verificationCode) {
            setErrors({ verificationCode: t("changeUserNumber.errors.otpRequired") });
            return;
        }

        if (verificationCode.length !== 5 && verificationCode.length !== 6) {
            setErrors({ verificationCode: t("changeUserNumber.errors.otpLength") });
            return;
        }

        setIsLoading(true);
        setErrors({});

        const payload = {
            newCountryCode: "+98",
            newPhone: normalizePhone(newPhoneNumber),
            otp: Number(verificationCode),
        };

        try {
            const response = await verifyChangePhone(payload, accessToken);

            if (response?.accessToken) {
                localStorage.setItem("accessToken", response.accessToken);
            }
            if (response?.refreshToken) {
                localStorage.setItem("refreshToken", response.refreshToken);
            }

            setSuccessData(response);
            setCountdown(0);
            setTestOtp(null);
            setErrors({});
        } catch (error) {
            const errorCode = getBackendErrorCode(error);

            if (errorCode === "Authorize404" || errorCode === "invalidOtp") {
                setErrors({ verificationCode: t("changeUserNumber.errors.otpInvalid") });
                return;
            }

            if (errorCode === "Authorize423" || errorCode === "otpExpired") {
                setCountdown(0);
                setErrors({ verificationCode: t("changeUserNumber.errors.otpExpired") });
                return;
            }

            if (errorCode === "Authorize406" || errorCode === "userOtpNotFound") {
                setStep(1);
                setVerificationCode("");
                setCountdown(0);
                setTestOtp(null);
                setErrors({ general: t("changeUserNumber.errors.otpNotFound") });
                return;
            }

            if (errorCode === "Authorize442" || errorCode === "phoneNotAvailable") {
                setStep(1);
                setNewPhoneNumber("");
                setVerificationCode("");
                setCountdown(0);
                setTestOtp(null);
                setErrors({ newPhoneNumber: t("changeUserNumber.errors.phoneTaken") });
                return;
            }

            if (errorCode === "Authorize444" || errorCode === "samePhoneNumber") {
                setStep(1);
                setNewPhoneNumber("");
                setVerificationCode("");
                setCountdown(0);
                setTestOtp(null);
                setErrors({ newPhoneNumber: t("changeUserNumber.errors.phoneSame") });
                return;
            }

            if (error?.response?.status === 422) {
                const detail = error?.response?.data?.detail;
                if (Array.isArray(detail)) {
                    const validationErrors = {};
                    detail.forEach((item) => {
                        const field = item?.loc?.[item.loc.length - 1];
                        if (field === "otp") validationErrors.verificationCode = item?.msg || t("changeUserNumber.errors.otpFieldInvalid");
                        if (field === "newPhone" || field === "newCountryCode") validationErrors.newPhoneNumber = item?.msg || t("changeUserNumber.errors.newPhoneFieldInvalid");
                    });
                    if (Object.keys(validationErrors).length > 0) {
                        setErrors(validationErrors);
                        return;
                    }
                }
                setErrors({ general: t("changeUserNumber.errors.invalidData") });
                return;
            }

            setErrors({ general: getBackendMessage(error) || t("changeUserNumber.errors.changeFailed") });
        } finally {
            setIsLoading(false);
        }
    };

    const handleResendCode = () => {
        if (countdown > 0 || isLoading) return;
        setStep(1);
        setVerificationCode("");
        setTestOtp(null);
        setErrors({});
    };

    const handleBackToStepOne = () => {
        if (isLoading) return;
        setStep(1);
        setVerificationCode("");
        setTestOtp(null);
        setErrors({});
    };

    // =====================================================
    // Success Screen
    // =====================================================
    if (successData) {
        const finalPhone = successData?.phone ? `0${successData.phone}` : newPhoneNumber;

        return (
            <SidebarProvider>
                <StarField />

                <nav className="border-b border-slate-200 dark:!border-white/10 
                                bg-white dark:!bg-gray-900/80 
                                backdrop-blur-xl dark:!backdrop-blur-none
                                print:hidden flex items-center fixed top-0 right-0 left-0 z-30
                                shadow-[0_4px_16px_rgba(0,0,0,0.04)] dark:!shadow-[0_4px_16px_rgba(0,0,0,0.5)]">
                    <Header />
                </nav>

                <div className="vertical-menu rtl:right-0 fixed ltr:left-0 bottom-0 top-16 h-screen 
                                border-r border-slate-200 dark:!border-white/10 
                                bg-white dark:!bg-gray-900/80 
                                backdrop-blur-xl dark:!backdrop-blur-none
                                print:hidden z-20">
                    <UserSidebar />
                </div>

                <div className="main-content min-h-screen mt-16 flex flex-col lg:mr-64">
                    <div className="page-content flex-grow pt-2">
                        <div className="container-fluid px-4 sm:px-6 md:px-8">
                            {/* Page Title */}
                            <div className="grid grid-cols-1 mb-2">
                                <div className="flex items-center justify-between">
                                    <h4 className="mb-sm-0 text-lg sm:text-2xl font-bold 
                                                   text-slate-800 dark:!text-white 
                                                   drop-shadow-sm">
                                        {t("changeUserNumber.pageTitle")}
                                    </h4>
                                </div>
                            </div>

                            <h1 className="text-sm sm:text-base mr-2 
                                           text-slate-600 dark:!text-teal-100/80">
                                {t("changeUserNumber.pageSubtitle")}
                            </h1>
                        </div>

                        <div className="px-4 sm:px-6 md:px-8 py-8 sm:py-10">
                            {/* Success Card */}
                            <div className="mx-auto max-w-2xl">
                                <div
                                    className="rounded-3xl overflow-hidden
                                               bg-white
                                               border border-slate-200
                                               shadow-[0_20px_60px_rgba(15,118,110,0.15)]
                                               dark:!bg-gradient-to-br 
                                               dark:!from-teal-950 
                                               dark:!via-gray-950 
                                               dark:!to-black
                                               dark:!border dark:!border-teal-900/50
                                               dark:!shadow-[inset_0_1px_0_rgba(20,184,166,0.15),0_20px_60px_rgba(0,0,0,0.6)]"
                                    style={{ animation: "fadeInUp 0.6s ease-out" }}
                                >
                                    <div className="p-8 sm:p-12 text-center">
                                        {/* Success Icon */}
                                        <div className="mx-auto mb-6 flex h-24 w-24 items-center justify-center rounded-full 
                                                        bg-gradient-to-br from-teal-500/20 to-amber-500/20
                                                        border-2 border-teal-300/60 dark:!border-teal-900/50">
                                            <div className="flex h-16 w-16 items-center justify-center rounded-full 
                                                            bg-gradient-to-br from-teal-500 to-teal-700
                                                            shadow-xl shadow-teal-500/40">
                                                <svg
                                                    className="h-8 w-8 text-white"
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                    stroke="currentColor"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2.5}
                                                        d="M5 13l4 4L19 7"
                                                    />
                                                </svg>
                                            </div>
                                        </div>

                                        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight 
                                                       text-slate-800 dark:!text-white mb-3">
                                            {t("changeUserNumber.success.title")}
                                        </h2>

                                        <p className={`text-sm sm:text-base leading-6 
                                                      text-slate-600 dark:!text-teal-100/90 mb-8 ${isEnglish && 'font-sans'}`}>
                                            {t("changeUserNumber.success.subtitle")}
                                        </p>

                                        {/* Phone Box */}
                                        <div className="rounded-2xl 
                                                        bg-amber-50
                                                        border border-amber-200
                                                        shadow-[0_4px_16px_rgba(0,0,0,0.04)]
                                                        dark:!bg-teal-950
                                                        dark:!backdrop-blur-none
                                                        dark:!border dark:!border-teal-800/50
                                                        px-6 py-5 inline-block min-w-[250px]">
                                            <p className="text-xs text-slate-600 dark:!text-teal-200/80 mb-2">
                                                {t("changeUserNumber.success.newPhoneLabel")}
                                            </p>
                                            <p
                                                dir="ltr"
                                                className={`text-2xl font-bold text-teal-700 dark:!text-amber-300 ${isEnglish && 'font-sans'}`}
                                            >
                                                {finalPhone}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="[&_footer]:dark:!!bg-transparent [&_footer_*]:dark:!!text-teal-100 [&_footer]:dark:!!border-teal-900/50">
                        <Footer />
                    </div>
                </div>
            </SidebarProvider>
        );
    }

    // =====================================================
    // Main UI
    // =====================================================
    return (
        <SidebarProvider>
            <StarField />

            {/* Header */}
            <nav className="border-b border-slate-200 dark:!border-white/10 
                            bg-white dark:!bg-gray-900/80 
                            backdrop-blur-xl dark:!backdrop-blur-none
                            print:hidden flex items-center fixed top-0 right-0 left-0 z-30
                            shadow-[0_4px_16px_rgba(0,0,0,0.04)] dark:!shadow-[0_4px_16px_rgba(0,0,0,0.5)]">
                <Header />
            </nav>

            {/* Sidebar */}
            <div className="vertical-menu rtl:right-0 fixed ltr:left-0 bottom-0 top-16 h-screen 
                            border-r border-slate-200 dark:!border-white/10 
                            bg-white dark:!bg-gray-900/80 
                            backdrop-blur-xl dark:!backdrop-blur-none
                            print:hidden z-20">
                <UserSidebar />
            </div>

            {/* Main Content */}
            <div className="main-content min-h-screen mt-16 flex flex-col lg:ms-64">
                <div className="page-content flex-grow pt-2">
                    {/* Page Title */}
                    <div className="container-fluid px-4 sm:px-6 md:px-8 mt-10">
                        <div className="grid grid-cols-1 mb-2">
                            <div className="flex items-center justify-between">
                                <h4 className="mb-sm-0 text-lg sm:text-2xl font-bold 
                                               text-slate-800 dark:!text-white 
                                               drop-shadow-sm">
                                    {t("changeUserNumber.pageTitle")}
                                </h4>
                            </div>
                        </div>

                        <h1 className="text-sm sm:text-base mr-2 
                                       text-slate-600 dark:!text-teal-100/80">
                            {t("changeUserNumber.pageSubtitle")}
                        </h1>
                    </div>

                    {/* Form Area */}
                    <div className="px-4 sm:px-6 md:px-8 py-8 sm:py-10">

                        {/* Form Container */}
                        <div className="mx-auto max-w-2xl">
                            {/* Steps */}
                            <div className="mb-10 flex items-center justify-center">
                                {/* Step 1 */}
                                <div className="flex items-center">
                                    <div
                                        className={`flex h-12 w-12 items-center justify-center rounded-2xl text-base font-bold transition-all 
                                                    ${step >= 1
                                                ? "bg-gradient-to-br from-teal-500 to-teal-700 text-white shadow-xl shadow-teal-500/40 border-2 border-white/30"
                                                : "bg-white text-slate-400 border-2 border-slate-200 dark:!bg-white/10 dark:!text-white/60 dark:!border-white/20"
                                            }
                                            ${isEnglish && 'font-sans'}`}
                                    >
                                        1
                                    </div>
                                    <span
                                        className={`mr-3 text-sm font-bold ${step >= 1
                                            ? "text-teal-700 dark:!text-teal-300"
                                            : "text-slate-400 dark:!text-white/60"
                                            }`}
                                    >
                                        {t("changeUserNumber.steps.phone")}
                                    </span>
                                </div>

                                {/* Line */}
                                <div
                                    className={`mx-4 h-1 flex-1 max-w-[120px] rounded-full transition-all 
                                                ${step >= 2
                                            ? "bg-gradient-to-r from-teal-500 to-teal-600"
                                            : "bg-slate-200 dark:!bg-white/10"
                                        }`}
                                />

                                {/* Step 2 */}
                                <div className="flex items-center">
                                    <div
                                        className={`flex h-12 w-12 items-center justify-center rounded-2xl text-base font-bold transition-all 
                                                    ${step >= 2
                                                ? "bg-gradient-to-br from-teal-500 to-teal-700 text-white shadow-xl shadow-teal-500/40 border-2 border-white/30"
                                                : "bg-white text-slate-400 border-2 border-slate-200 dark:!bg-white/10 dark:!text-white/60 dark:!border-white/20"
                                            }
                                            ${isEnglish && 'font-sans'}`}
                                    >
                                        2
                                    </div>
                                    <span
                                        className={`mr-3 text-sm font-bold ${step >= 2
                                            ? "text-teal-700 dark:!text-teal-300"
                                            : "text-slate-400 dark:!text-white/60"
                                            }`}
                                    >
                                        {t("changeUserNumber.steps.otp")}
                                    </span>
                                </div>
                            </div>

                            {/* General Error */}
                            {errors.general && (
                                <div className="mb-6 flex items-center gap-3 p-4 rounded-2xl
                                                bg-red-50 dark:!bg-red-500/15
                                                border border-red-200 dark:!border-red-400/30
                                                text-red-700 dark:!text-red-200
                                                text-sm font-medium
                                                shadow-[0_4px_12px_rgba(239,68,68,0.08)]"
                                    style={{ animation: "fadeInUp 0.3s ease-out" }}>
                                    <div className="w-9 h-9 rounded-xl bg-red-500 flex items-center justify-center flex-shrink-0 shadow-md">
                                        <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                        </svg>
                                    </div>
                                    <span>{errors.general}</span>
                                </div>
                            )}

                            {/* =================================================
                                STEP 1
                            ================================================= */}
                            {step === 1 && (
                                <div className="space-y-6">
                                    {/* Current Phone */}
                                    <div style={{ animation: "fadeInUp 0.5s ease-out 0s both" }}>
                                        <label className="flex items-center gap-2 text-sm font-bold 
                                                          text-slate-800 dark:!text-white mb-3">
                                            <div className="w-7 h-7 rounded-xl 
                                                            bg-gradient-to-br from-teal-500 to-teal-700 
                                                            flex items-center justify-center 
                                                            shadow-lg shadow-teal-500/40">
                                                <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                                </svg>
                                            </div>
                                            {t("changeUserNumber.fields.oldPhoneLabel")} *
                                        </label>

                                        <input
                                            type="tel"
                                            value={oldPhoneNumber}
                                            onChange={handleOldPhoneChange}
                                            placeholder={t("changeUserNumber.fields.oldPhonePlaceholder")}
                                            dir="ltr"
                                            maxLength={11}
                                            disabled={isLoading}
                                            className={`w-full 
                                                       text-slate-900 dark:!text-white 
                                                       text-sm font-medium
                                                       bg-white dark:!bg-white/10
                                                       border-2 border-slate-200 dark:!border-white/20
                                                       rounded-2xl
                                                       px-5 py-4
                                                       transition-all duration-300
                                                       placeholder:text-slate-400 
                                                       dark:!placeholder:text-white/70
                                                       focus:outline-none 
                                                       focus:border-teal-500 dark:!focus:border-teal-400/70
                                                       focus:ring-4 focus:ring-teal-500/15
                                                       hover:border-teal-300
                                                       shadow-[0_2px_8px_rgba(0,0,0,0.04)]
                                                       dark:!shadow-[inset_0_1px_0_rgba(255,255,255,0.1),0_2px_8px_rgba(0,0,0,0.3)]
                                                       caret-teal-500 dark:!caret-teal-300
                                                       ${errors.oldPhoneNumber
                                                    ? "!border-red-400 !ring-4 !ring-red-400/15"
                                                    : ""
                                                }
                                                ${isEnglish && 'font-sans'}`}
                                        />

                                        {errors.oldPhoneNumber && (
                                            <p className="mt-2 text-sm text-red-500 font-bold flex items-center gap-1">
                                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01" />
                                                </svg>
                                                {errors.oldPhoneNumber}
                                            </p>
                                        )}
                                    </div>

                                    {/* New Phone */}
                                    <div style={{ animation: "fadeInUp 0.5s ease-out 0.1s both" }}>
                                        <label className="flex items-center gap-2 text-sm font-bold 
                                                          text-slate-800 dark:!text-white mb-3">
                                            <div className="w-7 h-7 rounded-xl 
                                                            bg-gradient-to-br from-teal-500 to-teal-700 
                                                            flex items-center justify-center 
                                                            shadow-lg shadow-teal-500/40">
                                                <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                                </svg>
                                            </div>
                                            {t("changeUserNumber.fields.newPhoneLabel")} *
                                        </label>

                                        <div className="flex gap-3">
                                            <input
                                                type="tel"
                                                value={newPhoneNumber}
                                                onChange={handleNewPhoneChange}
                                                placeholder={t("changeUserNumber.fields.newPhonePlaceholder")}
                                                dir="ltr"
                                                maxLength={11}
                                                disabled={isLoading}
                                                className={`min-w-0 flex-1
                                                           text-slate-900 dark:!text-white 
                                                           text-sm font-medium
                                                           bg-white dark:!bg-white/10
                                                           border-2 border-slate-200 dark:!border-white/20
                                                           rounded-2xl
                                                           px-5 py-4
                                                           transition-all duration-300
                                                           placeholder:text-slate-400 
                                                           dark:!placeholder:text-white/70
                                                           focus:outline-none 
                                                           focus:border-teal-500 dark:!focus:border-teal-400/70
                                                           focus:ring-4 focus:ring-teal-500/15
                                                           hover:border-teal-300
                                                           shadow-[0_2px_8px_rgba(0,0,0,0.04)]
                                                           dark:!shadow-[inset_0_1px_0_rgba(255,255,255,0.1),0_2px_8px_rgba(0,0,0,0.3)]
                                                           caret-teal-500 dark:!caret-teal-300
                                                           ${errors.newPhoneNumber
                                                        ? "!border-red-400 !ring-4 !ring-red-400/15"
                                                        : ""
                                                    }
                                                    ${isEnglish && 'font-sans'}`}
                                            />

                                            <button
                                                type="button"
                                                onClick={handleSendCode}
                                                disabled={isLoading || countdown > 0}
                                                className={`shrink-0 rounded-2xl px-6 py-4 text-sm font-bold text-white
                                                           transition-all duration-500
                                                           ${isLoading || countdown > 0
                                                        ? "cursor-not-allowed bg-slate-300 dark:!bg-gray-700/60"
                                                        : "bg-gradient-to-r from-teal-600 via-teal-500 to-amber-500 bg-[length:200%_100%] hover:bg-[position:100%_0] shadow-[0_8px_24px_rgba(20,184,166,0.5)] hover:shadow-[0_12px_32px_rgba(20,184,166,0.6)] hover:scale-[1.02] active:scale-[0.98]"
                                                    }`}
                                            >
                                                {isLoading ? (
                                                    <span className="flex items-center gap-2 whitespace-nowrap">
                                                        <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24">
                                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l2.647-2.647z" />
                                                        </svg>
                                                        {t("changeUserNumber.buttons.sending")}
                                                    </span>
                                                ) : countdown > 0 ? (
                                                    <span dir="ltr" className="font-mono">
                                                        {formatCountdown(countdown)}
                                                    </span>
                                                ) : (
                                                    t("changeUserNumber.buttons.sendCode")
                                                )}
                                            </button>
                                        </div>

                                        {errors.newPhoneNumber && (
                                            <p className="mt-2 text-sm text-red-500 font-bold flex items-center gap-1">
                                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01" />
                                                </svg>
                                                {errors.newPhoneNumber}
                                            </p>
                                        )}

                                        {countdown > 0 && (
                                            <p className="mt-2 text-xs text-slate-600 dark:!text-teal-200/80">
                                                {t("changeUserNumber.resendHint", {
                                                    time: formatCountdown(countdown),
                                                })}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* =================================================
                                STEP 2
                            ================================================= */}
                            {step === 2 && (
                                <div className="space-y-6">
                                    {/* Info */}
                                    <div className="rounded-2xl 
                                                    bg-teal-50 dark:!bg-teal-500/10
                                                    border-2 border-teal-200 dark:!border-teal-400/30
                                                    px-5 py-4
                                                    shadow-[0_4px_12px_rgba(20,184,166,0.08)]"
                                        style={{ animation: "fadeInUp 0.5s ease-out both" }}>
                                        <p className="text-sm font-medium text-teal-800 dark:!text-teal-100 mb-1">
                                            {t("changeUserNumber.sentTo")}
                                        </p>
                                        <p
                                            dir="ltr"
                                            className={`font-bold text-lg text-teal-700 dark:!text-amber-300 ${isEnglish && 'font-sans'}`}
                                        >
                                            {newPhoneNumber.toLocaleString('en-US')}
                                        </p>
                                    </div>

                                    {/* DEV OTP */}
                                    {isDevelopment && testOtp !== null && (
                                        <div className="rounded-2xl 
                                                        bg-amber-50 dark:!bg-amber-500/15
                                                        border-2 border-amber-200 dark:!border-amber-400/30
                                                        px-5 py-4
                                                        shadow-[0_4px_12px_rgba(251,191,36,0.08)]"
                                            style={{ animation: "fadeInUp 0.5s ease-out 0.1s both" }}>
                                            <div className="flex items-center justify-between gap-4">
                                                <div>
                                                    <p className="text-xs font-bold text-slate-700 dark:!text-amber-200/90">
                                                        {t("changeUserNumber.devOtpLabel")}
                                                    </p>
                                                    <p
                                                        dir="ltr"
                                                        className={`mt-1 text-2xl font-bold tracking-widest 
                                                                   text-teal-700 dark:!text-amber-300 ${isEnglish && 'font-sans'}`}
                                                    >
                                                        {testOtp}
                                                    </p>
                                                </div>

                                                <button
                                                    type="button"
                                                    onClick={() => setVerificationCode(String(testOtp))}
                                                    className="rounded-xl px-5 py-2.5 text-sm font-bold text-white
                                                               bg-gradient-to-r from-teal-600 to-teal-500
                                                               shadow-lg shadow-teal-500/40
                                                               hover:shadow-xl hover:scale-105
                                                               active:scale-95 transition-all duration-300"
                                                >
                                                    {t("changeUserNumber.buttons.useTestCode")}
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    {/* OTP */}
                                    <div style={{ animation: "fadeInUp 0.5s ease-out 0.2s both" }}>
                                        <label className="flex items-center gap-2 text-sm font-bold 
                                                          text-slate-800 dark:!text-white mb-3">
                                            <div className="w-7 h-7 rounded-xl 
                                                            bg-gradient-to-br from-teal-500 to-teal-700 
                                                            flex items-center justify-center 
                                                            shadow-lg shadow-teal-500/40">
                                                <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                                                </svg>
                                            </div>
                                            {t("changeUserNumber.fields.otpLabel")} *
                                        </label>

                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            autoComplete="one-time-code"
                                            value={verificationCode}
                                            onChange={handleOtpChange}
                                            placeholder={t("changeUserNumber.fields.otpPlaceholder")}
                                            dir="ltr"
                                            maxLength={6}
                                            disabled={isLoading}
                                            className={`w-full 
                                                       text-center text-2xl tracking-[0.5em] font-bold
                                                       text-slate-900 dark:!text-white 
                                                       bg-white dark:!bg-white/10
                                                       border-2 border-slate-200 dark:!border-white/20
                                                       rounded-2xl
                                                       px-5 py-4
                                                       transition-all duration-300
                                                       placeholder:text-slate-400 placeholder:text-sm placeholder:tracking-normal placeholder:font-medium
                                                       dark:!placeholder:text-white/70
                                                       focus:outline-none 
                                                       focus:border-teal-500 dark:!focus:border-teal-400/70
                                                       focus:ring-4 focus:ring-teal-500/15
                                                       hover:border-teal-300
                                                       shadow-[0_2px_8px_rgba(0,0,0,0.04)]
                                                       dark:!shadow-[inset_0_1px_0_rgba(255,255,255,0.1),0_2px_8px_rgba(0,0,0,0.3)]
                                                       caret-teal-500 dark:!caret-teal-300
                                                       ${errors.verificationCode
                                                    ? "!border-red-400 !ring-4 !ring-red-400/15"
                                                    : ""
                                                }
                                                ${isEnglish && 'font-sans'}`}
                                        />

                                        {errors.verificationCode && (
                                            <p className="mt-2 text-sm text-red-500 font-bold text-center flex items-center justify-center gap-1">
                                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01" />
                                                </svg>
                                                {errors.verificationCode}
                                            </p>
                                        )}
                                    </div>

                                    {/* Countdown */}
                                    {countdown > 0 && (
                                        <p className={`text-center text-xs text-slate-600 dark:!text-teal-200/80$ ${isEnglish && 'font-sans'}`}>
                                            {t("changeUserNumber.resendAvailableIn", {
                                                time: formatCountdown(countdown).toLocaleString('en-US'),
                                            })}
                                        </p>
                                    )}

                                    {/* Resend */}
                                    {countdown === 0 && (
                                        <div className="text-center">
                                            <button
                                                type="button"
                                                onClick={handleResendCode}
                                                disabled={isLoading}
                                                className="text-sm font-bold 
                                                           text-teal-700 dark:!text-amber-300 
                                                           underline decoration-teal-500/50 dark:!decoration-amber-400/50 decoration-2 underline-offset-4
                                                           hover:text-teal-600 dark:!hover:text-amber-200
                                                           disabled:text-slate-400 
                                                           transition-all duration-300"
                                            >
                                                {t("changeUserNumber.buttons.resendCode")}
                                            </button>
                                        </div>
                                    )}

                                    {/* Submit */}
                                    <button
                                        type="button"
                                        onClick={handleSubmit}
                                        disabled={
                                            isLoading ||
                                            (verificationCode.length !== 5 &&
                                                verificationCode.length !== 6)
                                        }
                                        className={`relative w-full 
                                                   py-4 px-6
                                                   rounded-2xl
                                                   text-white font-bold text-base
                                                   transition-all duration-500
                                                   overflow-hidden
                                                   group
                                                   ${isLoading ||
                                                (verificationCode.length !== 5 &&
                                                    verificationCode.length !== 6)
                                                ? "cursor-not-allowed bg-slate-300 dark:!bg-gray-700/60 text-slate-500 dark:!text-white/60"
                                                : "bg-gradient-to-r from-teal-600 via-teal-500 to-amber-500 bg-[length:200%_100%] hover:bg-[position:100%_0] shadow-[0_8px_24px_rgba(20,184,166,0.5)] hover:shadow-[0_12px_32px_rgba(20,184,166,0.6)] hover:scale-[1.02] active:scale-[0.98]"
                                            }`}
                                    >
                                        {!isLoading &&
                                            (verificationCode.length === 5 ||
                                                verificationCode.length === 6) && (
                                                <span className="absolute inset-0 
                                                                 bg-gradient-to-r from-transparent via-white/30 to-transparent 
                                                                 -translate-x-full group-hover:translate-x-full
                                                                 transition-transform duration-1000" />
                                            )}

                                        <div className="relative flex items-center justify-center gap-3">
                                            {isLoading ? (
                                                <>
                                                    <svg className="h-5 w-5 animate-spin" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l2.647-2.647z" />
                                                    </svg>
                                                    {t("changeUserNumber.buttons.submitting")}
                                                </>
                                            ) : (
                                                t("changeUserNumber.buttons.submit")
                                            )}
                                        </div>
                                    </button>

                                    {/* Back */}
                                    <button
                                        type="button"
                                        onClick={handleBackToStepOne}
                                        disabled={isLoading}
                                        className="block w-full text-center text-sm font-bold
                                                   text-slate-600 dark:!text-teal-200/80
                                                   hover:text-teal-700 dark:!hover:text-teal-300
                                                   disabled:cursor-not-allowed disabled:opacity-50
                                                   transition-all duration-300"
                                    >
                                        {t("changeUserNumber.buttons.changePhone")}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="[&_footer]:dark:!!bg-transparent [&_footer_*]:dark:!!text-teal-100 [&_footer]:dark:!!border-teal-900/50">
                    <Footer />
                </div>
            </div>

            <style jsx>{`
                @keyframes fadeInUp {
                    from {
                        opacity: 0;
                        transform: translateY(20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
            `}</style>
        </SidebarProvider>
    );
};

export default ChangeUserNumber;